import React, { FC, memo, useMemo } from 'react';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import RemoteImage from '@/modules/_core/components/images/Remote-image';
import Link from 'next/link';
import { FileVariantsEnum, UserDto } from '@sweatpals/shared';
import useLink from '@/modules/_core/hooks/useLink';
import { eventAuthorsStyles } from './event-authors.styles';
import { Showable, useStyles } from '@sweatpals/react-ui-kit';

interface EventAuthorsProps {
	host: UserDto;
	coHost?: UserDto;
	sx?: SxProps<Theme>;
	avatarsSx?: SxProps<Theme>;
	avatarWidth?: number;
	avatarHeight?: number;
	textSx?: SxProps<Theme>;
	onHostLinkClick?: (userName: string) => void;
}

const EventAuthors: FC<EventAuthorsProps> = memo((props) => {
	const {
		host,
		coHost,
		sx,
		textSx,
		avatarsSx,
		avatarWidth = 37,
		avatarHeight = 37,
		onHostLinkClick
	} = props;
	const { makeHostLink } = useLink();

	const hostPageUrl = useMemo(() => makeHostLink({ userName: host.userName }), [host.userName, makeHostLink]);
	const coHostPageUrl = useMemo(() => makeHostLink({ userName: coHost?.userName }), [coHost?.userName, makeHostLink]);

	const styles = useStyles(eventAuthorsStyles);

	return (
		<Box
			sx={[
				{
					display: 'flex',
					alignItems: 'center',
					gap: '8px'
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<Box
				sx={[
					{
						position: 'relative',
						height: coHost ? '47px' : '37px',
						width: coHost ? '61px' : '37px'
					},
					...(Array.isArray(avatarsSx) ? avatarsSx : [avatarsSx]),
				]}
			>
				<Showable show={coHost}>
					<RemoteImage
						src={coHost?.avatarId}
						alt={coHost?.fullName}
						width={avatarWidth}
						height={avatarHeight}
						sx={{ ...styles.authorAvatar, top: 0, right: 0 }}
						variant={FileVariantsEnum.XS}
					/>
				</Showable>
				<RemoteImage
					src={host.avatarId}
					alt={host.fullName}
					width={avatarWidth}
					height={avatarHeight}
					sx={{ ...styles.authorAvatar, left: 0, bottom: 0 }}
					variant={FileVariantsEnum.XS}
				/>
			</Box>
			<Typography
				sx={[
					...(Array.isArray(styles.authorNameText) ? styles.authorNameText : [styles.authorNameText]),
					...(Array.isArray(textSx) ? textSx : [textSx]),
				]}
			>
				<Link href={hostPageUrl} passHref legacyBehavior>
					<a onClick={() => onHostLinkClick?.(host.userName)}>
						{host.fullName}
					</a>
				</Link>
				<Showable show={coHost}>
					{` and `}
					<Link href={coHostPageUrl} passHref legacyBehavior>
						<a onClick={() => onHostLinkClick?.(coHost?.userName)}>
							{coHost?.fullName}
						</a>
					</Link>
				</Showable>
			</Typography>
		</Box>
	);
});

export default EventAuthors;
